import * as moment from 'moment';
import { FormEvent, useEffect, useState } from 'react';
import { IConvertDonationType, IListItem } from '@nkr/models/index';
import { DonationType } from '@nkr/enums/index';
import { Button, FormControl, FormGroup, Alert, Col, ControlLabel, Form, Modal, HelpBlock, DropdownButton, MenuItem } from 'react-bootstrap'

interface IListItemWithVisible extends IListItem {
    visible: boolean,
    subtitle: string
}

interface IProps {
    show: boolean,
    donorId: number,
    isPaired: boolean,
    hasIntended: boolean,
    isExpertSystem: boolean,
    hasDashReferral: boolean,
    isAdmin: boolean,
    typeList: IListItem[],
    onSave: (data: IConvertDonationType, donorId: number) => void,
    onCancel: () => void
}

const directDonationTypeList = [
    DonationType.NkrInternalDirect,
    DonationType.RemoteDirect,
    DonationType.KflInternalDirect,
    DonationType.KflRemoteDirect
];

const ConvertDonorType = (props: IProps) => {
    const [donationType, setDonationType] = useState(0);
    const [donationDate, setDonationDate] = useState('');
    const [dirty, setDirty] = useState(false);

    useEffect(() => {
        if (!props.show) {
            setDonationType(0);
            setDonationDate('');
            setDirty(false);
        }

    }, [props.show]);

    const handleDonationTypeChange = (value: any) => {
        setDonationType(parseInt(value));
    };

    const handleDonationDateChange = (e: FormEvent<FormControl & HTMLInputElement>) => {
        setDonationDate(e.currentTarget.value);
    };

    const validate = (dirty: boolean) => {
        let donationTypeValid = dirty ? donationType != 0 : true;
        let donationDateValid = dirty ? validDonationDate() : true;

        let isValid = donationTypeValid && donationDateValid;

        return { donationTypeValid, donationDateValid, isValid };
    };

    const validDonationDate = () => {
        if (directDonationTypeList.includes(donationType)) {
            if (!donationDate) {
                return false;
            } else if (moment(donationDate, 'MM-DD-YYYY').isValid()) {
                return false;
            }
        }

        return true;
    };

    const handleSave = () => {
        setDirty(true);

        let isValid = validate(true).isValid

        if (isValid) {
            let data: IConvertDonationType = {
                donationType,
                donationDate
            };

            props.onSave(data, props.donorId)
        }
    };

    const { donationTypeValid, donationDateValid, isValid } = validate(dirty);

    const typeList = props.typeList.map(x => {
        let visible = false;
        let subtitle = '';

        switch (x.id) {
            case DonationType.DirectedDonor:
                visible = !props.isPaired && props.hasIntended;
                break;

            case DonationType.NkrInternalDirect:
                visible = props.isPaired && props.hasDashReferral && props.hasIntended;
                break;

            case DonationType.RemoteDirect:
                visible = props.isAdmin && props.hasIntended;
                break;

            case DonationType.KflInternalDirect:
                visible = props.isAdmin && props.hasIntended && props.isExpertSystem;
                break;

            case DonationType.StandardVoucher:
                visible = true;
                subtitle = props.isPaired ? 'Recipient will be a voucher holder' : '';
                break;

            case DonationType.FamilyVoucher:
                visible = true;
                subtitle = props.isPaired ? 'Recipient will detach as a \'waitlist patient\'' : '';
                break;

            case DonationType.NonDirectedDonor:
                visible = true;
                subtitle = props.isPaired ? 'Recipient will detach as a \'waitlist patient\'' : '';
                break;

            default:
                visible = true;
        }

        let item: IListItemWithVisible = {
            ...x,
            subtitle,
            visible
        };

        return item;
    }).filter(x => props.isAdmin ? true : x.visible);

    const selectedType = typeList.find(x => x.id == donationType);
    const selectedTypeTitle = selectedType == null ? '- select type -' : selectedType.description;
    const selectedTypeSubtitle = selectedType == null ? '' : selectedType.subtitle;

    return (
        <Modal show={props.show} backdrop='static' onHide={props.onCancel}>
            <Modal.Header closeButton>
                <Modal.Title>Convert {props.isPaired ? 'Pair' : 'Donor'}</Modal.Title>
            </Modal.Header>

            <Modal.Body>
                {!isValid && (
                    <Alert bsStyle='danger' style={{ paddingTop: 7.5, paddingBottom: 7.5 }}>
                        The fields in <strong>red</strong> are required...
                    </Alert>
                )}

                <Form horizontal>
                    <FormGroup validationState={donationTypeValid == false ? 'error' : null}>
                        <Col xs={12} lg={3} componentClass={ControlLabel}>Convert To:</Col>
                        <Col xs={12} lg={9}>
                            <DropdownButton title={selectedTypeTitle} id='convert-type' onSelect={handleDonationTypeChange}>
                                {typeList.map(item =>
                                    <MenuItem eventKey={item.id}>
                                        {item.description}
                                        {item.subtitle ? <div style={{ fontSize: 12, paddingLeft: 10 }}>({item.subtitle})</div> : null}
                                    </MenuItem>
                                )}
                            </DropdownButton>
                            {selectedTypeSubtitle && (
                                <HelpBlock>({selectedTypeSubtitle})</HelpBlock>
                            )}
                        </Col>
                    </FormGroup>

                    {directDonationTypeList.includes(donationType) && (
                        <FormGroup validationState={donationDateValid == false ? 'error' : null}>
                            <Col xs={12} lg={3} componentClass={ControlLabel}>Donation Date:</Col>
                            <Col xs={12} lg={9}>
                                <FormControl
                                    type='date'
                                    value={donationDate}
                                    onChange={handleDonationDateChange}
                                />
                                <HelpBlock>This is required</HelpBlock>
                            </Col>
                        </FormGroup>
                    )}
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button bsStyle='primary' onClick={handleSave}>Convert</Button>
                <Button onClick={props.onCancel}>Cancel</Button>
            </Modal.Footer>
        </Modal>
    );
}

export default ConvertDonorType;